import { withFocusRing } from "@components/Accessibility/FocusRing/FocusRing";
import { Link } from "@components/Link/Link";
import { StyledButtonLink } from "@components/Styled/Button";
import {
  ComponentProps,
  GatsbyCustomImage,
  ResolvedLink,
} from "@ts/components";
import { CustomImage } from "@bared/ui";
import { CustomImageType } from "@bared/groq";
import { ThemeAspectRatio } from "@ts/theme";
import { clsx } from "clsx";
import React from "react";
import { Image } from "./Image";

export type ImageCTAProps = ComponentProps & {
  alt: string;
  blog?: boolean;
  buttonLink?: string;
  buttonText: string;
  image?: GatsbyCustomImage;
  sanityImage?: CustomImageType;
  imageLink?: ResolvedLink;
  instagramHandle?: string;
  onClick?: () => void;
  onClickImage?: () => void;
  ratio?: "none" | Extract<keyof ThemeAspectRatio, string>;
};

const Wrapper = withFocusRing((props: any) => (
  <div {...props} className={clsx(props.className, "relative")} />
));

const Desktop = ({ children }: { children: React.ReactNode }) => (
  <div className="inset-0 transition-layout duration-slow pointer-events-none absolute hidden items-center justify-center opacity-0 group-hover:pointer-events-auto group-hover:z-10 group-hover:opacity-100 md:flex">
    {children}
  </div>
);

const Mask = (props: any) => (
  <Link
    {...props}
    className={clsx(
      props.className,
      "inset-0 absolute bg-secondary opacity-70",
      props.blog && "cursor-pointer",
    )}
  />
);

const Button = (props: any) => (
  <StyledButtonLink
    {...props}
    className={clsx(
      props.className,
      "z-10 w-full max-w-[232px]",
      props.blog ? "text-white" : "bg-white text-primary",
    )}
  />
);

const InstagramHandleStyled = ({ children }: { children: React.ReactNode }) => (
  <div className="break-all pt-mini text-caption">{children}</div>
);

const ImageCTA = ({
  image,
  buttonText,
  buttonLink,
  alt,
  onClick,
  blog,
  onClickImage,
  imageLink,
  ratio,
  instagramHandle,
  sanityImage,
}: ImageCTAProps) => {
  return (
    <Wrapper className="group" tabIndex={0}>
      {buttonLink ? (
        <Desktop>
          {buttonLink && buttonText && (
            <Mask to={buttonLink} blog={blog} tabIndex={-1} title={alt} />
          )}
          {buttonLink ? (
            <Button
              className="underlined-white"
              to={buttonLink}
              blog={blog}
              height="72-56"
              textSize="16"
            >
              <span>{buttonText}</span>
            </Button>
          ) : buttonText && onClick ? (
            <Button onClick={onClick} height="72-56" textSize="16">
              {buttonText}
            </Button>
          ) : null}
        </Desktop>
      ) : null}
      {sanityImage ? (
        <CustomImage data={sanityImage} data-testid="customimage" />
      ) : (
        <Image
          alt={alt}
          image={image}
          ratio={ratio || `11/12`}
          onClick={onClickImage}
          imageLink={imageLink}
        />
      )}
      {instagramHandle?.length ? (
        <InstagramHandleStyled>@{instagramHandle}</InstagramHandleStyled>
      ) : null}
    </Wrapper>
  );
};

export default ImageCTA;
